import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Admission,
  Alert,
  ServiceGroupItem,
  Service,
  SubscriptionPayload,
  ServiceGroup,
} from '@quality24/inpatient-typings';

import { useToast } from '@quality24/design-system';
import { AuthContext } from '@/inpatient-patient-pwa/types/auth';
import {
  getFloorId,
  getRoomId,
} from '@/inpatient-patient-pwa/services/storage';
import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';
import navbarContext from './navbarContext';

/**
 * GraphQL authenticate mutation
 */
const SELECT_QUERY = gql`
  query FetchNavbarData($admissionId: UUID!, $floorId: UUID!) {
    admission: admissionById(id: $admissionId) {
      id
      roomId
      endedAt
    }

    activeAlerts: alerts(
      filter: {
        admissionId: { equalTo: $admissionId }
        resolvedAt: { isNull: true }
        cancelledAt: { isNull: true }
        group: {
          group: {
            active: { equalTo: true }
            locationIds: { contains: [$floorId] }
          }
        }
      }
    ) {
      totalCount
    }
  }
`;

const ALERT_SUBSCRIPTION = gql`
  subscription onAlertChanged($admissionId: UUID!) {
    alertChanges: alertChangesByAdmissionId(admissionId: $admissionId) {
      event
      alertData {
        id
        resolvedAt
        cancelledAt
        reasonId
        details
        group {
          id
          service {
            id
            reason
            reasonI18N
          }
          group {
            id
            locationIds
          }
        }
      }
    }
  }
`;

const ADMISSION_CHANGED_SUBSCRIPTION = gql`
  subscription onRoomChanged($admissionId: UUID!, $roomId: UUID!) {
    admissionChanges: admissionChanges(
      admissionId: $admissionId
      roomId: $roomId
    ) {
      admission: admissionData {
        id
        roomId
        endedAt
      }
    }
  }
`;

export interface QueryResult {
  admission: Pick<Admission, 'id' | 'roomId' | 'endedAt'> | null;
  activeAlerts: {
    totalCount: number;
  };
}

interface AlertSubscriptionResult {
  subscriptionData: {
    data: {
      alertChanges: SubscriptionPayload<
        'alertData',
        Pick<Alert, 'id' | 'resolvedAt' | 'cancelledAt' | 'details'> & {
          group: Pick<ServiceGroupItem, 'id'> & {
            service: Required<Pick<Service, 'id' | 'reason' | 'reasonI18N'>>;
            group: Pick<ServiceGroup, 'id' | 'locationIds'>;
          };
        }
      >;
    };
  };
}

interface AdmissionSubscriptionResult {
  subscriptionData: {
    data: {
      admissionChanges: SubscriptionPayload<'admission', Admission>;
    };
  };
}

export interface Props {
  children: React.ReactNode;
}

/**
 * Handles a new alert changes event
 */
const handleAlertChanges = (
  prev: QueryResult,
  { subscriptionData }: AlertSubscriptionResult,
  floorId: string,
  t: TFunction,
  notifier?: (message: string) => void,
) => {
  if (!subscriptionData.data) return prev;
  const { event, alertData: alert } = subscriptionData.data.alertChanges;

  // Check if alert is from a group we have access
  if (!alert.group.group.locationIds.includes(floorId)) {
    return prev;
  }

  const totalCount = prev?.activeAlerts?.totalCount || 0;

  switch (event) {
    case 'update':
      if (!alert.resolvedAt && !alert.cancelledAt) {
        return prev;
      }
      if (alert.cancelledAt) {
        const {
          group: { service },
          details: { cancelReason },
        } = alert;
        notifier?.(
          t('alerts.cancelledToast', {
            reason: service.reasonI18N,
            cancelReason: cancelReason || t('alerts.notInformed'),
          }),
        );
      }

      return {
        ...prev,
        activeAlerts: {
          ...prev.activeAlerts,
          totalCount: totalCount - 1,
        },
      };

    case 'insert':
      return {
        ...prev,
        activeAlerts: {
          ...prev.activeAlerts,
          totalCount: totalCount + 1,
        },
      };

    default:
      return prev;
  }
};

const handleAdmissionChanges =
  (logout: AuthContext['logout']) =>
  (prev: QueryResult, { subscriptionData }: AdmissionSubscriptionResult) => {
    if (!subscriptionData.data) return prev;

    const { admission } = subscriptionData.data.admissionChanges;
    const roomId = getRoomId();

    if (admission && (admission.roomId !== roomId || admission.endedAt)) {
      logout();
    }

    return prev;
  };

/**
 * <NavbarProvider> component
 */
const NavbarProvider: React.FunctionComponent<Props> = ({ children }) => {
  const { warning } = useToast();
  const { t } = useTranslation();

  const {
    user: { admissionId },
    logout,
  } = useAuth();
  const roomId = getRoomId();
  const floorId = getFloorId();

  const { data, loading, error, subscribeToMore, called } =
    useQuery<QueryResult>(SELECT_QUERY, {
      variables: { admissionId, floorId },
      skip: !admissionId || !floorId,
    });

  React.useEffect(() => {
    if (!admissionId) return () => null;

    const unsubscribe = subscribeToMore({
      document: ALERT_SUBSCRIPTION,
      variables: { admissionId },
      updateQuery: (queryResult, subscriptionResult: AlertSubscriptionResult) =>
        handleAlertChanges(
          queryResult,
          subscriptionResult,
          floorId,
          t,
          (message) => warning(message),
        ),
    });
    return unsubscribe;
  }, [admissionId, floorId, subscribeToMore, warning, t]);

  React.useEffect(() => {
    if (roomId === null) {
      return () => null;
    }
    const unsubscribe = subscribeToMore({
      document: ADMISSION_CHANGED_SUBSCRIPTION,
      variables: { admissionId, roomId },
      updateQuery: handleAdmissionChanges(logout),
    });
    return unsubscribe;
  }, [admissionId, roomId, logout, subscribeToMore]);

  React.useEffect(() => {
    // If no admission found or admission finished, logout
    if (
      !loading &&
      !error &&
      called &&
      (!data?.admission || data.admission.endedAt)
    ) {
      logout();
      return;
    }
    // If patient changed room, logout
    if (
      !loading &&
      !error &&
      called &&
      (!data?.admission || data.admission.roomId)
    ) {
      if (roomId !== null && data?.admission?.roomId !== roomId) {
        logout();
      }
    }
  }, [loading, error, logout, data?.admission, roomId, called]);

  const value = React.useMemo(
    () => ({
      activeAlerts: data?.activeAlerts?.totalCount || 0,
    }),
    [data?.activeAlerts?.totalCount],
  );

  return (
    <navbarContext.Provider value={value}>{children}</navbarContext.Provider>
  );
};

export default NavbarProvider;
